import { useEffect, useState } from "react";
import { toast } from "sonner";
import PartnerGate from "./Gate";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, inr } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

const EMPTY_CAND = { name: "", email: "", phone: "", headline: "", skills: "", experience_years: 0, current_ctc: "", expected_ctc: "", notice_period: "", location: "", screening_notes: "", recommendation: "recommended" };

export default function Openings() {
  const [openings, setOpenings] = useState([]);
  const [submitFor, setSubmitFor] = useState(null);
  const [cand, setCand] = useState(EMPTY_CAND);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/partner/openings").then(({ data }) => setOpenings(data.openings)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submitCandidate = async () => {
    setBusy(true);
    try {
      await api.post("/partner/submissions", {
        ...cand, job_id: submitFor.id,
        skills: cand.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_years: Number(cand.experience_years),
        current_ctc: Number(cand.current_ctc || 0), expected_ctc: Number(cand.expected_ctc || 0),
      });
      toast.success("Candidate submitted to employer");
      setSubmitFor(null);
      setCand(EMPTY_CAND);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PartnerGate>
      {() => (
        <div data-testid="partner-openings">
          {openings.length === 0 ? (
            <EmptyState title="No new openings." description="Approved openings matching your plan scope will appear here." cta="Refresh Openings" onCta={load} testId="openings-empty" />
          ) : (
            <div className="grid gap-4">
              {openings.map((j) => (
                <div key={j.id} className="bg-white border border-border/60 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4 card-hover" data-testid={`opening-${j.id}`}>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-[#0B1B3D]">{j.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {j.company_name} · {j.location} · {j.experience_min}–{j.experience_max} yrs · {j.vacancies} vacancies ·{" "}
                      <span className="capitalize">{j.recruitment_payment_model}</span>
                    </p>
                    <p className="text-xs mt-2 text-[#2563EB] font-semibold">{inr(j.salary_min)} – {inr(j.salary_max)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">Est. payout / joining</p>
                    <p className="font-heading font-bold text-lg text-emerald-700">{inr(j.partner_payout_estimate)}</p>
                    <Button size="sm" className="rounded-full mt-2 bg-[#0B1B3D]" onClick={() => setSubmitFor(j)} data-testid={`submit-candidate-${j.id}`}>
                      Submit Candidate {j.my_submissions > 0 && `(${j.my_submissions})`}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Dialog open={!!submitFor} onOpenChange={() => setSubmitFor(null)}>
            <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="submit-candidate-dialog">
              <DialogHeader>
                <DialogTitle>Submit candidate — {submitFor?.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Name *</Label><Input value={cand.name} onChange={(e) => setCand({ ...cand, name: e.target.value })} className="mt-1.5" data-testid="cand-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={cand.email} onChange={(e) => setCand({ ...cand, email: e.target.value })} className="mt-1.5" data-testid="cand-email" /></div>
                  <div><Label>Phone</Label><Input value={cand.phone} onChange={(e) => setCand({ ...cand, phone: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Headline</Label><Input value={cand.headline} onChange={(e) => setCand({ ...cand, headline: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Skills (comma)</Label><Input value={cand.skills} onChange={(e) => setCand({ ...cand, skills: e.target.value })} className="mt-1.5" data-testid="cand-skills" /></div>
                  <div><Label>Experience (yrs)</Label><Input type="number" value={cand.experience_years} onChange={(e) => setCand({ ...cand, experience_years: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Current CTC (₹)</Label><Input type="number" value={cand.current_ctc} onChange={(e) => setCand({ ...cand, current_ctc: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Expected CTC (₹)</Label><Input type="number" value={cand.expected_ctc} onChange={(e) => setCand({ ...cand, expected_ctc: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Notice period</Label><Input value={cand.notice_period} onChange={(e) => setCand({ ...cand, notice_period: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Location</Label><Input value={cand.location} onChange={(e) => setCand({ ...cand, location: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div><Label>Screening notes</Label><Textarea rows={3} value={cand.screening_notes} onChange={(e) => setCand({ ...cand, screening_notes: e.target.value })} className="mt-1.5" data-testid="cand-notes" /></div>
                <div>
                  <Label>Recommendation</Label>
                  <Select value={cand.recommendation} onValueChange={(v) => setCand({ ...cand, recommendation: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="cand-recommendation"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strongly_recommended">Strongly Recommended</SelectItem>
                      <SelectItem value="recommended">Recommended</SelectItem>
                      <SelectItem value="hold">Hold</SelectItem>
                      <SelectItem value="not_recommended">Not Recommended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={submitCandidate} disabled={busy || !cand.name || !cand.email} className="w-full rounded-full bg-[#0B1B3D]" data-testid="submit-candidate-btn">
                  {busy ? "Submitting…" : "Submit to Employer"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </PartnerGate>
  );
}
